/**
 * Skeleton Card - Pit-wall style shimmer loading state
 * Shows instantly while real data loads, then crossfades
 */

import { memo } from 'react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';

interface SkeletonCardProps {
  index?: number;
  className?: string;
}

/**
 * Skeleton card with subtle directional shimmer
 * Designed to match ResultCard layout exactly to prevent reflow
 */
export const SkeletonCard = memo(function SkeletonCard({
  index = 0,
  className,
}: SkeletonCardProps) {
  // Stagger the shimmer animation start based on index
  const animationDelay = `${(index % 8) * 0.1}s`;

  return (
    <div
      className={clsx(
        'relative rounded-xl overflow-hidden',
        'bg-workshop-surface border border-workshop-border/50',
        className
      )}
    >
      {/* Image skeleton - fixed aspect ratio to prevent layout shift */}
      <div className="relative aspect-[4/3] overflow-hidden">
        <div
          className="absolute inset-0 bg-workshop-bg skeleton-shimmer"
          style={{ animationDelay }}
        />
        {/* Number badge skeleton */}
        <div className="absolute top-3 left-3 w-8 h-6 rounded bg-workshop-surface/50" />
        {/* Confidence badge skeleton */}
        <div className="absolute top-3 right-3 w-12 h-6 rounded bg-workshop-surface/50" />
      </div>

      {/* Content skeleton */}
      <div className="p-4 space-y-3">
        {/* Title */}
        <div
          className="h-5 w-3/4 rounded bg-workshop-bg skeleton-shimmer"
          style={{ animationDelay }}
        />

        {/* Tags row */}
        <div className="flex gap-2">
          <div
            className="h-6 w-16 rounded-full bg-workshop-bg skeleton-shimmer"
            style={{ animationDelay: `${(index % 8) * 0.1 + 0.05}s` }}
          />
          <div
            className="h-6 w-20 rounded-full bg-workshop-bg skeleton-shimmer"
            style={{ animationDelay: `${(index % 8) * 0.1 + 0.1}s` }}
          />
        </div>

        {/* Detail tags */}
        <div className="flex gap-2">
          <div
            className="h-5 w-14 rounded bg-workshop-bg/50 skeleton-shimmer"
            style={{ animationDelay: `${(index % 8) * 0.1 + 0.15}s` }}
          />
          <div
            className="h-5 w-12 rounded bg-workshop-bg/50 skeleton-shimmer"
            style={{ animationDelay: `${(index % 8) * 0.1 + 0.2}s` }}
          />
        </div>

        {/* Date */}
        <div
          className="h-4 w-20 rounded bg-workshop-bg/30 skeleton-shimmer"
          style={{ animationDelay: `${(index % 8) * 0.1 + 0.25}s` }}
        />
      </div>
    </div>
  );
});

/**
 * Skeleton grid - fills viewport instantly
 */
interface SkeletonGridProps {
  count?: number;
  className?: string;
}

export const SkeletonGrid = memo(function SkeletonGrid({
  count = 12,
  className,
}: SkeletonGridProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.08 }}
      className={clsx(
        'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4',
        className
      )}
    >
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} index={i} />
      ))}
    </motion.div>
  );
});

export default SkeletonCard;
